import axios from 'axios';
import { getAccessToken, setAccessToken, clearAccessToken, setTokenExpiryCallback } from './tokenManager';
import { FAST_API_BASE_URL } from 'constants';

const API_BASE_URL = process.env.REACT_APP_FAST_API_BASE_URL;

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

const NO_REDIRECT_ENDPOINTS = [
  '/api/user/session',
  '/api/user/re-login',
  '/api/user/login',
  '/api/signup',
  '/api/verify-otp',
  '/api/save-password',
  '/api/save-google-user',
  '/api/reset-password',
   '/save-registration',
  '/register'
];

const AUTH_ENDPOINTS = [
  '/api/user/login',
  '/api/user/re-login',
  '/api/user/session',
    '/api/save-password',
  '/api/save-google-user',
  '/api/reset-password',
   '/save-registration',
  '/register'
];


const SESSION_CACHE_TTL     = 30_000; 
const VISIBILITY_THROTTLE   = 60_000;
const REFRESH_DEBOUNCE      = 2_000;

let sessionState = {
  lastSync : 0,
  session  : null,
  loading  : false,
  promise  : null,
};

let isRefreshing      = false;
let failedQueue       = [];
let refreshPromise    = null;
let lastRefreshCall   = 0;


let sessionExpiredHandler   = null;
let expiryCallbackScheduled = false;
let lastVisibilityCheck     = 0; 


const processQueue = (error, token = null) => {
  failedQueue.forEach(p => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

export const setSessionExpiredHandler = (handler) => {
  sessionExpiredHandler = handler;
};

const broadcastSessionChange = (message) => {
  if (typeof window === 'undefined') return;
  try {
    const ch = new BroadcastChannel('ansera-session');
    ch.postMessage(message);
    ch.close();
  } catch (e) {
    console.error('Broadcast failed:', e);
  }
};

export const getSession = async (options = {}) => {
  const now = Date.now();
  const { broadcast = true, force = false } = options;

  if (!force && sessionState.session && (now - sessionState.lastSync) < SESSION_CACHE_TTL) {
    return sessionState.session;
  }

  if (sessionState.loading && sessionState.promise) {
    return sessionState.promise;
  }

  sessionState.loading = true;
  sessionState.lastSync = now;

  sessionState.promise = (async () => {
    try {
      const response = await axiosInstance.get(
        `${FAST_API_BASE_URL}/api/user/session`,
        { withCredentials: true, headers: { 'X-Session-Request': 'true' } }
      );

      if (response.status === 200 && response.data?.result) {
        sessionState.session = response.data.result;
        if (response.data.result.access_token) {
          setAccessToken(response.data.result.access_token);
        }
        if (broadcast) {
          broadcastSessionChange({ event: 'session', data: { trigger: 'getSession' } });
        }
        return sessionState.session;
      }

      sessionState.session = null;
      return null;
    } catch (err) {
      console.error('❌ Session fetch failed:', err);
      sessionState.session = null;
      return null;
    } finally {
      sessionState.loading = false;
      sessionState.promise = null;
    }
  })();

  return sessionState.promise;
};

if (typeof window !== 'undefined') {
  const channel = new BroadcastChannel('ansera-session');
  channel.onmessage = (event) => {
    if (event.data?.event === 'session') {
      sessionState.lastSync = 0;
    }
  };
}

if (typeof window !== 'undefined') {
  getSession();

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return;

  const now = Date.now();
  
  if (now - sessionState.lastSync < SESSION_CACHE_TTL) {
    return;
  }

  if (now - lastVisibilityCheck < VISIBILITY_THROTTLE) {
    return;
  }

  lastVisibilityCheck = now;
  sessionState.lastSync = 0;
  getSession();
});

  window.addEventListener('online', () => {
    sessionState.lastSync = 0;
    getSession();
  });
}

const performRefresh = async () => {
  const now = Date.now();

  if (now - lastRefreshCall < REFRESH_DEBOUNCE && refreshPromise) {
    return refreshPromise;
  }

  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  lastRefreshCall = now;
  isRefreshing    = true;

  refreshPromise = (async () => {
    try {
      const res = await axiosInstance.get(
        `${FAST_API_BASE_URL}/api/user/session`,
        {
          withCredentials  : true,
          _retry           : true,
          headers          : { 'X-Session-Request': 'true' },
        }
      );

      if (res.status === 200 && res.data?.result?.access_token) {
        const newToken = res.data.result.access_token;
        setAccessToken(newToken);
        sessionState.session  = res.data.result;
        sessionState.lastSync = Date.now();
        return newToken;
      }

      throw new Error('Refresh failed – invalid response');
    } catch (err) {
      console.error('❌ Token refresh failed:', err);
      processQueue(err, null);
      throw err;
    } finally {
      isRefreshing = false;
      setTimeout(() => { refreshPromise = null; }, 500);
    }
  })();

  return refreshPromise;
};

axiosInstance.interceptors.request.use(
  async (config) => {
    if (
      config.url.includes('/api/user/session') ||
      config.url.includes('/api/user/re-login')
    ) {
      return config;
    }

    const isAuthEndpoint = AUTH_ENDPOINTS.some(ep => config.url.includes(ep));

    if (!isAuthEndpoint) {
      let token = getAccessToken();
      if (!token && !config._sessionRetry) {
        config._sessionRetry = true;
        const session = sessionState.loading && sessionState.promise
          ? await sessionState.promise
          : await getSession();

        if (session?.access_token) {
          token = session.access_token;
          setAccessToken(token);
        }
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    if (
      config.url.includes('/api/signup')        ||
      config.url.includes('/api/verify-otp')    ||
      config.url.includes('/api/save-google-user') ||
      config.url.includes('/api/register')
    ) {
      config.headers['X-API-KEY'] = process.env.REACT_APP_API_KEY;
    }

    if (config.url.includes('/api/user/login')) {
      try {
        const domainToken  = localStorage.getItem('domain_token');
        const secretToken  = localStorage.getItem('secret');
        if (domainToken)  config.headers['DOMAIN-TOKEN'] = domainToken;
        if (secretToken)  config.headers['SECRET-TOKEN']  = secretToken;
      } catch (e) {
        console.error('Error adding domain token:', e);
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => {
    if (response.data?.result?.access_token) {
      setAccessToken(response.data.result.access_token);
      sessionState.session  = response.data.result;
      sessionState.lastSync = Date.now();
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const url             = originalRequest?.url || '';

    if (originalRequest?.headers?.['X-Session-Request']) return Promise.reject(error);
    if (originalRequest._retry)                           return Promise.reject(error);

    const shouldRedirect  = !NO_REDIRECT_ENDPOINTS.some(ep => url.includes(ep));
    const isAuthEndpoint  = AUTH_ENDPOINTS.some(ep => url.includes(ep));

    if (error.response?.status === 401 && shouldRedirect && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({
              resolve: (token) => {
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(axiosInstance(originalRequest));
              },
              reject,
            });
          });
        }

        const newToken = await performRefresh();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);
        broadcastSessionChange({ event: 'session', data: { trigger: 'refresh' } });
        return axiosInstance(originalRequest);

      } catch (refreshError) {
        clearAccessToken();
        sessionState.session = null;
        if (sessionExpiredHandler) sessionExpiredHandler();
        if (shouldRedirect && !window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

setTokenExpiryCallback(async () => {
  if (!sessionState.session || expiryCallbackScheduled) return;

  expiryCallbackScheduled = true;

  setTimeout(async () => {
    try {
      await performRefresh();
    } catch (err) {
      console.error('Auto-refresh failed:', err);
      if (sessionExpiredHandler) sessionExpiredHandler();
    } finally {
      expiryCallbackScheduled = false;
    }
  }, 100);
});


export const refreshSession = async () => {
  sessionState.lastSync = 0;
  return getSession({ force: true });
};

export const clearSessionState = () => {
  sessionState = { lastSync: 0, session: null, loading: false, promise: null };
  clearAccessToken();
};

export default axiosInstance;